import type { AgentCapabilityGuide } from "@productlane/lib/src/agent-recommendations"
import type {
	AgentEvidenceOutcome,
	AgentRecommendationType,
} from "@productlane/lib/src/agent-recommendations"
import { AGENT_CONNECTOR_SETUP_PATHS } from "@productlane/lib/src/agent-recommendations"

// Bug Inspect fixes sit beside the recommendations under their own type.
export type AgentItemType =
	| AgentRecommendationType
	| "CODE"
	| "DOCS_SUGGESTION"
	| "SETUP"

export type AgentTab = "all" | "docs" | "guidance" | "code" | "engineering"

export const AGENT_TABS: { id: AgentTab; label: string }[] = [
	{ id: "all", label: "All" },
	{ id: "docs", label: "Docs" },
	{ id: "guidance", label: "Guidance" },
	{ id: "code", label: "Code" },
	{ id: "engineering", label: "Connector" },
]

export function isAgentTab(value: string | undefined): value is AgentTab {
	return AGENT_TABS.some((tab) => tab.id === value)
}

const TAB_TYPES: Record<Exclude<AgentTab, "all">, AgentItemType[]> = {
	docs: ["CONTENT", "CONTRADICTION", "DOCS_SUGGESTION"],
	guidance: ["GUIDANCE"],
	code: ["CODE"],
	engineering: ["DATA", "ACTION"],
}

export function matchesTab(type: AgentItemType, tab: AgentTab): boolean {
	return tab === "all" || TAB_TYPES[tab].includes(type)
}

export function countByTab(
	types: readonly AgentItemType[],
): Record<AgentTab, number> {
	const counts: Record<AgentTab, number> = {
		all: 0,
		docs: 0,
		guidance: 0,
		code: 0,
		engineering: 0,
	}
	for (const type of types) {
		for (const tab of ["docs", "guidance", "code", "engineering"] as const) {
			if (TAB_TYPES[tab].includes(type)) {
				counts[tab]++
				counts.all++
			}
		}
	}
	return counts
}

/** The agent's opening line on a docs suggestion it wrote. */
export function docsSuggestionIntro(source: string): string {
	switch (source) {
		case "PR_MERGED":
			return "A pull request just merged, so I drafted a docs change to match it."
		case "COMMIT_TO_MAIN":
			return "A commit just went to main, so I drafted a docs change to match it."
		case "ISSUE_COMPLETED":
			return "An issue was just released, so I drafted a docs change to match it."
		case "CHANGELOG":
			return "I drafted a docs change from your latest changelog."
		case "THREAD":
			return "Customers asked about this, so I drafted a docs change."
		default:
			return "I drafted the docs change your team asked for."
	}
}

/** The opening line on a docs change a person wrote, or the API left as a draft. */
export function proposalIntro({
	authorName,
	isOrphanDraft,
}: {
	authorName?: string | null
	isOrphanDraft: boolean
}): string {
	if (isOrphanDraft)
		return "This draft came in through the API and waits for review."
	const name = authorName?.trim()
	return name
		? `${name} suggested this change.`
		: "Someone on your team suggested this change."
}

export function docsSourceLabel(source: string): string {
	switch (source) {
		case "PR_MERGED":
			return "From a merged PR"
		case "COMMIT_TO_MAIN":
			return "From a commit"
		case "ISSUE_COMPLETED":
			return "From a completed issue"
		case "CHANGELOG":
			return "From a changelog"
		case "THREAD":
			return "From conversations"
		default:
			return "From your team"
	}
}

// Engineering recommendations: gaps only a connector an engineer builds can close.
export const ENGINEERING_COPY = {
	emptyTitle: "Every gap is with engineering",
	emptyText: "Data and actions the agent needs from your systems wait here.",
	sectionTitle: "What the agent needs",
	need: "Need",
	suggestion: "Suggested connector",
	connect: (system: string) => `Connect ${system}`,
	addConnector: "Add a connector",
	addPostgresQuery: "Add a Postgres query",
	questions: "Questions it could then answer",
	acceptButton: "Accept and create issue",
	teamLabel: "Team",
	assigneeLabel: "Assignee",
	titleLabel: "Title",
	createButton: "Create issue",
	openInLinear: "Open in Linear",
	createdToast: (identifier: string) => `Created ${identifier}`,
	createFailedToast: "Couldn't create the Linear issue",
	noTeamsToast: "Connect Linear to create issues",
	openSettings: "Open Linear settings",
}

/** "ENG-512 · Todo", or the identifier alone while the status is unknown. */
export function linkedIssueLabel(
	identifier: string,
	statusName: string | null | undefined,
): string {
	return statusName ? `${identifier} · ${statusName}` : identifier
}

export const AGENT_EMPTY_STATES: Record<
	AgentTab,
	{ title: string; text: string }
> = {
	all: {
		title: "All caught up",
		text: "New chats and agent suggestions show up here as they arrive.",
	},
	docs: {
		title: "Docs are up to date",
		text: "Help center changes the agent suggests wait here for review.",
	},
	guidance: {
		title: "Guidance is up to date",
		text: "Changes to how the agent replies wait here for review.",
	},
	code: {
		title: "Every fix is reviewed",
		text: "Pull requests the agent drafts for bugs wait here.",
	},
	engineering: {
		title: ENGINEERING_COPY.emptyTitle,
		text: ENGINEERING_COPY.emptyText,
	},
}

export const DECLINE_NOTE_PLACEHOLDER =
	"What should the agent do instead? (optional)"

export function typeLabel(type: AgentItemType): string {
	switch (type) {
		case "CODE":
			return "Code"
		case "SETUP":
			return "Setup"
		case "CONTENT":
		case "CONTRADICTION":
		case "DOCS_SUGGESTION":
			return "Docs"
		case "GUIDANCE":
			return "Guidance"
		case "DATA":
		case "ACTION":
			return "Connector"
	}
}

export function conversationsLabel(count: number): string {
	return count === 1 ? "1 conversation" : `${count} conversations`
}

export function rowMeta(
	type: AgentRecommendationType,
	conversationCount: number,
): string {
	return `${typeLabel(type)} · From ${conversationsLabel(conversationCount)}`
}

/** An accepted card reads what it measurably answered once the review has counted it. */
export function acceptedRowMeta(
	type: AgentRecommendationType,
	conversationCount: number,
	measuredResolved: number | undefined,
): string {
	if (measuredResolved === undefined) return rowMeta(type, conversationCount)
	return `${typeLabel(type)} · Answered ${measuredResolved} since accepted`
}

export function codeRowMeta(state: "review" | "running"): string {
	return state === "review"
		? "Code · Awaiting approval"
		: "Code · Reproducing the bug"
}

export function proposalRowMeta(isOrphanDraft: boolean): string {
	return isOrphanDraft ? "Docs · API draft" : "Docs · From your team"
}

export const RUNNING_FIX_TEXT =
	"Bug Inspect is reproducing this bug in a sandbox. Its draft pull request shows up here once the fix passes."

export const CODE_FIX_REPORT_LABEL = "Reported in chat"

export const CODE_FIX_INTRO =
	"A customer reported a bug, so I opened a pull request that fixes it."

export function outcomeLabel(outcome: AgentEvidenceOutcome): string {
	switch (outcome) {
		case "handed_off":
			return "Handed off"
		case "wrong_answer":
			return "Wrong answer"
		case "no_answer":
			return "Not answered"
		case "teammate_answered":
			return "Your team answered"
	}
}

const SINCE_FORMAT = new Intl.DateTimeFormat("en-US", {
	month: "short",
	day: "numeric",
})

export function factsLine({
	conversationCount,
	customerCount,
	since,
	impactPerMonth,
}: {
	conversationCount: number
	customerCount: number
	since: string | null | undefined
	impactPerMonth: number
}): string {
	const customers =
		customerCount === 1 ? "1 customer" : `${customerCount} customers`
	const sinceDate = since ? new Date(since) : null
	const sinceText =
		sinceDate && !Number.isNaN(sinceDate.getTime())
			? ` since ${SINCE_FORMAT.format(sinceDate)}`
			: ""
	const impact =
		impactPerMonth === 1
			? "About 1 more resolution a month."
			: `About ${impactPerMonth} more resolutions a month.`
	return `${conversationsLabel(conversationCount)} from ${customers}${sinceText}. ${impact}`
}

export function checkLine(
	answered: number | null | undefined,
	total: number | null | undefined,
): string | null {
	if (!total || typeof answered !== "number") return null
	return `With this change the agent answered ${answered} of the ${total}`
}

export function reviewProgressLabel(
	read: number | null | undefined,
	total: number | null | undefined,
): string {
	if (!total || typeof read !== "number") return "Starting the review"
	return `Reviewing conversations, ${read} of ${total}`
}

export function typeBadgeLabel(type: AgentRecommendationType): string {
	switch (type) {
		case "CONTENT":
		case "CONTRADICTION":
			return "Docs fix"
		case "GUIDANCE":
			return "Guidance fix"
		case "DATA":
		case "ACTION":
			return "Engineering fix"
	}
}

export interface RecommendationStat {
	label: string
	value: string
	hint?: string
}

export function recommendationStats({
	impactPerMonth,
	conversationCount,
	customerCount,
	since,
	rerunAnswered,
	rerunTotal,
}: {
	impactPerMonth: number
	conversationCount: number
	customerCount: number
	since: string | null | undefined
	rerunAnswered: number | null | undefined
	rerunTotal: number | null | undefined
}): RecommendationStat[] {
	const sinceDate = since ? new Date(since) : null
	const sinceHint =
		sinceDate && !Number.isNaN(sinceDate.getTime())
			? `Since ${SINCE_FORMAT.format(sinceDate)}`
			: undefined
	const third: RecommendationStat =
		rerunTotal && typeof rerunAnswered === "number"
			? {
					label: "Answered with the change",
					value: `${rerunAnswered} of ${rerunTotal}`,
				}
			: {
					label: customerCount === 1 ? "Customer" : "Customers",
					value: String(customerCount),
				}
	return [
		{ label: "Extra resolutions a month", value: `+${impactPerMonth}` },
		{
			label: conversationCount === 1 ? "Conversation" : "Conversations",
			value: String(conversationCount),
			hint: sinceHint,
		},
		third,
	]
}

/** The idea's fixed first line: who asked and since when, then where the answer came from. */
export function ideaOrigin({
	conversationCount,
	customerCount,
	since,
	learnedFrom,
}: {
	conversationCount: number
	customerCount: number
	since: string | null | undefined
	learnedFrom: "code" | "teammate" | undefined
}): string {
	const customers =
		customerCount === 1 ? "1 customer" : `${customerCount} customers`
	const sinceDate = since ? new Date(since) : null
	const sinceText =
		sinceDate && !Number.isNaN(sinceDate.getTime())
			? ` since ${SINCE_FORMAT.format(sinceDate)}`
			: ""
	const asked = `${customers} asked about this in ${conversationsLabel(conversationCount)}${sinceText}.`
	if (learnedFrom === "code")
		return `${asked} I found the answer in your code on GitHub.`
	if (learnedFrom === "teammate")
		return `${asked} Your team answered ${conversationCount === 1 ? "it" : "these"} by hand.`
	return asked
}

// The model writes the opening line per idea; older ideas have none and open on
// the summary, since one canned line on every card reads as a template.
export function ideaIntro(intro: string | null | undefined): string | null {
	return intro?.trim() || null
}

export interface IdeaPlanParts {
	lead: string
	// The one article the change touches, shown as a mention between lead and tail.
	article: { title: string; articleId: string | null } | null
	tail: string
}

// Sits between the example and the change: what the agent would do and why.
export function ideaPlanParts(
	type: AgentRecommendationType,
	articles: readonly {
		kind: string
		title: string
		articleId?: string | null
	}[],
): IdeaPlanParts {
	const sentence = (lead: string): IdeaPlanParts => ({
		lead,
		article: null,
		tail: "",
	})
	if (type === "GUIDANCE") {
		return sentence(
			"I'd add one rule to my guidance, so I answer this the same way every time.",
		)
	}
	if (type === "DATA" || type === "ACTION") {
		return sentence(
			"I'd file this for your engineers with what I need to answer it myself.",
		)
	}
	const [only] = articles
	if (articles.length !== 1 || !only) {
		return sentence(
			"I'd update the docs, so I can answer this myself next time.",
		)
	}
	const article = { title: only.title, articleId: only.articleId ?? null }
	switch (only.kind) {
		case "CREATE":
			return {
				lead: "I'd write a new article,",
				article: { ...article, articleId: null },
				tail: ", so I can answer this myself next time.",
			}
		case "DELETE":
			return {
				lead: "I'd remove",
				article,
				tail: ", so my answers match the product.",
			}
		default:
			return {
				lead: "I'd update",
				article,
				tail: ", so I can answer this myself next time.",
			}
	}
}

export function ideaPlan(
	type: AgentRecommendationType,
	articles: readonly { kind: string; title: string }[],
): string {
	const { lead, article, tail } = ideaPlanParts(type, articles)
	return article ? `${lead} "${article.title}"${tail}` : lead
}

// Shown in place of the buttons once anyone on the team decided, so a second
// person with the card open sees it is already handled.
export function closedIdeaLabel(
	status: string,
	closedBy: string | null | undefined,
): string {
	const by = closedBy?.trim() ? ` by ${closedBy.trim()}` : ""
	switch (status) {
		case "ACCEPTED":
			return `Accepted${by}`
		case "DISMISSED":
			return `Declined${by}`
		default:
			return "No longer open"
	}
}

/** Where the card sends a teammate to give the agent this ability, and what the button says. */
export function connectorSetup(
	guide: Pick<AgentCapabilityGuide, "connector" | "system">,
): {
	path: string
	label: string
} {
	const connector = guide.connector ?? "custom_connector"
	const system = guide.system?.trim()
	return {
		path: AGENT_CONNECTOR_SETUP_PATHS[connector],
		label: system
			? ENGINEERING_COPY.connect(system)
			: connector === "postgres"
				? ENGINEERING_COPY.addPostgresQuery
				: ENGINEERING_COPY.addConnector,
	}
}
